/**
 * Logo Component
 * Displays the Complin logo and icon
 */

import React from 'react';
import { ICON_URL } from '@/constants/branding';

interface LogoProps {
  /** Size variant: 'sm' (20px), 'md' (24px), 'lg' (32px), or custom size */
  size?: 'sm' | 'md' | 'lg' | number;
  /** Show text next to logo */
  showText?: boolean;
  /** Custom className for the container */
  className?: string;
  /** Icon only mode (for collapsed sidebar, favicon, etc.) */
  iconOnly?: boolean;
}

const Logo: React.FC<LogoProps> = ({ 
  size = 'md', 
  showText = true, 
  className = '',
  iconOnly = false 
}) => {
  
  // Size mapping
  const sizeMap = {
    sm: 20,
    md: 24,
    lg: 32,
  };
  
  const iconSize = typeof size === 'number' ? size : sizeMap[size];
  const textSize = iconSize * 0.85; // Text slightly smaller than icon
  
  const [imageError, setImageError] = React.useState(false);

  if (iconOnly) {
    if (imageError) {
      // Fallback to placeholder if image fails to load
      return (
        <div 
          className={`rounded-full bg-black flex-shrink-0 ${className}`}
          style={{ width: iconSize, height: iconSize }}
        />
      );
    }
    
    return (
      <img 
        src={ICON_URL}
        alt="Complin"
        className={className}
        style={{ width: iconSize, height: iconSize }}
        onError={() => setImageError(true)}
      />
    );
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {imageError ? (
        // Fallback to placeholder if image fails to load
        <div 
          className="rounded-full bg-black flex-shrink-0"
          style={{ width: iconSize, height: iconSize }}
        />
      ) : (
        <img 
          src={ICON_URL}
          alt="Complin"
          style={{ width: iconSize, height: iconSize }}
          className="flex-shrink-0"
          onError={() => setImageError(true)}
        />
      )}
      {showText && (
        <span 
          className="font-bold text-gray-900 lowercase flex-shrink-0" 
          style={{ 
            fontSize: `${textSize}px`,
            letterSpacing: '-0.5px', 
            fontFamily: 'Inter, sans-serif' 
          }}
        >
          complin
        </span>
      )}
    </div>
  );
};

export default Logo;

